import { createSelector } from 'reselect';
import { OrderedMap, List, Map } from 'immutable';

// Types
import { UpdateAiTemplateType, UpdateAiTemplateSectionType } from 'Shared/types/UpdateAiTemplate.type';
import { UpdateAiSettingType } from 'Shared/types/UpdateAiSettingType.type';

// Constants
import UpdateAiSettingsConstants from 'Shared/constants/UpdateAiSettings.constants';

function createParameterSelector(selector) {
  return (_, params) => selector(params);
}

const getDefaultTemplate = createParameterSelector(params => params.defaultTemplate);
const getSlug = createParameterSelector(params => params.slug);
const getTemplateSlug = createParameterSelector(params => params?.templateSlug);
const getTemplateSectionSlugs = createParameterSelector(params => params?.templateSectionSlugs);
const getDraft = createParameterSelector(params => params?.draft);
const getTemplateType = createParameterSelector(params => params?.templateType);

const getUpdateAi = state => state.updateAiSettings;

export const getUpdateAiTemplates = createSelector([getUpdateAi], updateAiSettings =>
  updateAiSettings.getIn([UpdateAiSettingsConstants.State.TEMPLATE_BY_TEMPLATE_SLUG], OrderedMap())
);

export const getUpdateAiSettings = createSelector([getUpdateAi], updateAiSettings =>
  updateAiSettings.getIn([UpdateAiSettingsConstants.State.UPDATE_AI_SETTINGS_BY_SLUG], OrderedMap())
);

export const getTemplateSections = createSelector([getUpdateAi], updateAiSettings =>
  updateAiSettings.getIn([UpdateAiSettingsConstants.State.TEMPLATE_SECTIONS_BY_SLUG], OrderedMap())
);

export const getUpdateAiReports = createSelector([getUpdateAi], updateAiSettings =>
  updateAiSettings.getIn([UpdateAiSettingsConstants.State.REPORTS_BY_REPORT_SLUG], OrderedMap())
);

export const getCurrentlyGeneratingSlugs = createSelector([getUpdateAi], updateAiSettings =>
  updateAiSettings.getIn([UpdateAiSettingsConstants.State.CURRENTLY_GENERATING_SECTION_SLUGS], List())
);

export const getUpdateAiTemplatesByDefaultTemplate = createSelector(
  [getUpdateAiTemplates, getDefaultTemplate],
  (templates, defaultTemplate) => {
    return templates
      .filter(item => item.get('defaultTemplate') === defaultTemplate)
      .valueSeq()
      .toJS() as UpdateAiTemplateType[];
  }
);

export const getDefaultUpdateAiTemplatesByTemplateType = createSelector(
  [getUpdateAiTemplates, getTemplateType],
  (templates, templateType) => {
    return templates
      .find(item => item.get('defaultTemplate') === true && item.get('templateType') === templateType, Map())
      ?.toJS() as UpdateAiTemplateType;
  }
);

export const getUpdateAiTempateBySlug = createSelector([getUpdateAiTemplates, getTemplateSlug], (templates, slug) => {
  const template = templates.get(slug);
  return template ? (template.toJS() as UpdateAiTemplateType) : null;
});

export const getUpdateAiSettingBySlug = createSelector([getUpdateAiSettings, getSlug], (updateAiSettings, slug) => {
  const updateAiSetting = updateAiSettings.get(slug);
  return updateAiSetting ? (updateAiSetting.toJS() as UpdateAiSettingType) : null;
});

export const getUpdateAiTemplateSections = createSelector(
  [getTemplateSections, getTemplateSectionSlugs],
  (sections, slugs = []) => {
    return sections
      ? (sections
          .filter(s => slugs.includes(s.get('slug')))
          .sort(s => s.get('position'))
          .valueSeq()
          .toJS() as UpdateAiTemplateSectionType[])
      : [];
  }
);

// export const getTemplateSectionBySlug = createSelector([getTemplateSections, getSlug], (sections, slug) => {
//   return sections.get(slug, Map());
// });

export const getReportbyUpdateAiSlug = createSelector([getUpdateAiReports, getSlug], (reports, slug) => {
  return reports.filter(report => report.get('updateAiSettingSlug') === slug);
});

export const getDraftReportByUpdateAiSlug = createSelector([getReportbyUpdateAiSlug, getDraft], (reports, draft) => {
  return reports.find(report => report.get('draft') === draft);
});

export const getUpdateAiReportsByDraft = createSelector([getUpdateAiReports, getDraft], (reports, draft) => {
  return reports.filter(report => report.get('draft') === draft);
});

export const getUpdateAiReportBySlug = createSelector([getUpdateAiReports, getSlug], (reports, slug) => {
  return reports.get(slug, null);
});
